import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

import { API_BASE } from '../lib/config';
const BACKEND_URL = API_BASE;

export default function CheckEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // メールアドレスを取得（リダイレクト元から）
  const { email, userType } = location.state || {};
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState({ success: null, message: '' });
  
  // メールアドレスがない場合は登録ページにリダイレクト
  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  const handleResendEmail = async () => {
    if (isResending) return;
    
    setIsResending(true);
    setResendStatus({ success: null, message: '' });
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/resend-verification`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      setResendStatus({
        success: response.data.success,
        message: response.data.message || '確認メールを再送信しました。',
      });
    } catch (error) {
      console.error('Error resending email:', error);
      const errorMessage = error.response?.data?.message || 
                         'メールの送信中にエラーが発生しました。しばらくしてからもう一度お試しください。';
      setResendStatus({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          確認メールを送信しました
        </h2>
        
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-blue-100">
              <EnvelopeIcon className="h-12 w-12 text-blue-600" />
            </div>
            
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {email} に確認メールを送信しました
            </h3>
            
            <p className="mt-2 text-sm text-gray-600">
              メールに記載されている確認リンクをクリックして、アカウントの登録を完了させてください。
            </p>
            
            <p className="mt-2 text-sm text-gray-600">
              メールが届かない場合、迷惑メールフォルダもご確認ください。
            </p>
            
            <div className="mt-6">
              <button
                type="button"
                onClick={handleResendEmail}
                disabled={isResending}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isResending ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isResending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    送信中...
                  </>
                ) : (
                  '確認メールを再送する'
                )}
              </button>
              
              {resendStatus.message && (
                <p className={`mt-2 text-sm ${
                  resendStatus.success ? 'text-green-600' : 'text-red-600'
                }`}>
                  {resendStatus.message}
                </p>
              )}
            </div>
            
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                ログインページに戻る
              </button>
            </div>
            
            <div className="mt-2">
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm font-medium text-gray-600 hover:text-gray-500"
              >
                別のアカウントで登録
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
